// src/prototype/components/hub/ScoringRationaleTab.tsx
// ============================================================
// SCORING RATIONALE TAB (Full-Content Drilldown v4)
//
// The 4-dimension taxonomy (Financial Impact · Operational Urgency · Strategic
// Alignment · Implementation Feasibility) → individually scored measures → per-measure
// rationale → "Show evidence" disclosure. Replaces the page-local 5-dimension mock.
//
// All content comes from the content adapter (BigQuery-shaped: client_use_case_scores +
// measure_results + client_measure_rationalizations). No data is built here.
//
// TWO PER-MEASURE MODES, driven by the data rather than by a prop:
//   · scale '1to5'   — dva-stage rows (Care Coordination / uc 1693). Real rationale prose,
//                      real weight_label + source_type chips, score as 1–5 pips. NEVER
//                      converted to 0–100.
//   · scale '0to100' — every other use case, unchanged.
//
// The former hardcoded "61% conf" badge is GONE. Stage carries no confidence for client 300,
// so the pill renders only when a real number is present; provenance is carried by the weight
// and source chips plus the page-level provenance line instead.
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import {
  DollarSign, Clock, Target, Zap, List, FileText, BarChart3,
  ChevronRight, ChevronUp, ChevronDown, Database, Sparkles, TrendingUp, AlertTriangle,
} from 'lucide-react';

import type { DecisionBrief } from '../../data/decisionBrief';
import { getScores, DIM_META } from '../../data/adapters';
import type {
  DimensionVM, EvidenceType, EvidenceVM, MeasureVM, PersonalizationDeltaRow, PriorityThemeRow,
  RationaleProvenanceRow,
} from '../../data/adapters';
import {
  adjustmentPhrase, burdenCue, collectEvidenceTraces, compositeBreakdown, formatMeasureScore, joinList,
  strongestAndWeakest, strongestInDimension,
} from '../../data/rationaleSummary';
import { AsOfChip } from './AsOfChip';
import { SectionHeaderBand } from './SectionHeaderBand';
import { BandedCard } from './BandedCard';
// The ONLY sanctioned accessor for a measure name on screen. `m.name` carries the
// warehouse string, which is what put banned terms on this page.
import { displayNameForKey } from '../../../data/coverage/measureDisplayNames';

const DIM_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  dollar: DollarSign, clock: Clock, target: Target, zap: Zap,
};
const EV_ICON: Record<EvidenceType, React.ComponentType<{ className?: string }>> = {
  survey: List, document: FileText, benchmark: BarChart3, calculation: Zap, assumption: Target, inference: Zap,
};

const confPill = (c: number) => (c >= 0.65 ? 'bg-emerald-50 text-emerald-700' : c >= 0.5 ? 'bg-amber-50 text-amber-700' : 'bg-rose-50 text-rose-700');
const scoreCls = (s: number) => (s >= 75 ? 'bg-[#18365E] text-white' : s >= 55 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800');
const pct = (c: number) => Math.round(c * 100);

const WEIGHT_CLS: Record<string, string> = {
  High: 'bg-[#18365E] text-white',
  Medium: 'bg-[#dbe3ee] text-[#18365E]',
  Low: 'bg-[#F5F7FA] text-[#717182] border border-[#e5e7eb]',
};

// ---- source chip labels -----------------------------------------------------
// The warehouse's raw source_type values are pipeline slugs ('llm', 'p4_delta', …). They are
// engineering vocabulary, not something to read aloud, so the chip shows a presentable label
// and the RAW value stays visible in the page-level provenance line.
//
// Extend this map as the pipeline emits new source types; anything unmapped falls back to a
// humanised form of the slug rather than disappearing or leaking an acronym.
// Keys are matched lower-cased, so the warehouse's casing variants ('llm' / 'LLM') both hit.
const SOURCE_LABEL: Record<string, string> = {
  llm: 'Model-evaluated',
  llm_generated: 'Model-evaluated', // stage variant, 13 use cases
  survey: 'Client survey',
  document: 'Client document',
  benchmark: 'Benchmark',
  inferred: 'Inferred',
  p4_delta: 'Personalization delta',
  p5_delta: 'Personalization delta',
};

/** 'some_new_type' → 'Some new type' — the fallback for an unmapped source_type. */
function humanizeSource(raw: string): string {
  const s = raw.replace(/[_-]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function sourceLabel(raw: string): string {
  return SOURCE_LABEL[raw.toLowerCase()] ?? humanizeSource(raw);
}

/** 1–5 stage score, rendered as pips. No 0–100 conversion anywhere on this path. */
function ScorePips({ value }: { value: number }) {
  const filled = Math.round(value);
  return (
    <span className="inline-flex items-center gap-1" title={`Stage score ${value} of 5`}>
      <span className="flex items-center gap-[3px]">
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={`w-[7px] h-[7px] rounded-full ${i <= filled ? 'bg-[#18365E]' : 'bg-[#dbe3ee]'}`}
          />
        ))}
      </span>
      <span className="text-[10px] font-bold text-[#18365E]">{value.toFixed(1)}<span className="text-[#717182] font-semibold">/5</span></span>
    </span>
  );
}

/** Raw pipeline trace lines (project, table, ids) are internal; they live in Data provenance. */
const INTERNAL_TRACE =
  /dva-stage|client_measure_rationalizations|measure_rationalizations|source_type|Stage scoring|Stage weighting|client_priority_themes|apdva|client-\d+ /i;

/** What the reader sees on an evidence row in place of the raw trace line. */
function evidenceSourceLabel(e: EvidenceVM, m: MeasureVM): string {
  if (!INTERNAL_TRACE.test(e.src)) return e.src;
  if (e.type === 'calculation') return 'Source: scoring calculation';
  return m.source ? `Source: ${sourceLabel(m.source).toLowerCase()} rationale` : 'Source: evaluation rationale';
}

/** Composite weight of a dimension as a whole percentage, from the weights that produced it. */
const dimWeightPct = (weights: Readonly<Record<DimensionVM['id'], number>>, id: DimensionVM['id']) =>
  Math.round(weights[id] * 100);

/** Dimension bar fill and label: stage rollups of 1 to 5 measures are a 1 to 5 mean. */
function dimBar(d: DimensionVM) {
  const fivePoint = d.rollup === 'measures' && d.measures.some((m) => m.evaluated && m.scale === '1to5');
  const max = fivePoint ? 5 : 100;
  return {
    widthPct: Math.min(100, (d.score / max) * 100),
    label: fivePoint ? `${d.score.toFixed(1)}/5` : d.score.toFixed(1),
  };
}

/** Short dimension names for the breakdown legend. */
const DIM_SHORT: Record<DimensionVM['id'], string> = {
  financial: 'Financial', urgency: 'Urgency', strategic: 'Strategic', feasibility: 'Feasibility',
};
/** Adaptive Navy at three strengths, Interactive Orange for feasibility (as in the dimension bars). */
const SEGMENT_FILL: Record<DimensionVM['id'], string> = {
  financial: '#18365E',
  urgency: 'rgba(24, 54, 94, 0.7)',
  strategic: 'rgba(24, 54, 94, 0.45)',
  feasibility: '#FF6E4C',
};

const measureDomId = (id: string) => `rationale-measure-${id}`;

function MeasureRow({ m, open, onToggle }: { m: MeasureVM; open: boolean; onToggle: () => void }) {
  const [showEv, setShowEv] = useState(false);
  const cue = burdenCue(m);

  // GAP. The source row for this measure was rejected as not-an-evaluation, so the page says so
  // plainly: catalog name, honest state, nothing else. No pips, no weight chip, no source chip, no
  // evidence, and never the warehouse score, which belongs to the reasoning that was rejected.
  if (!m.evaluated) {
    return (
      <div id={measureDomId(m.id)} className="bg-white rounded-lg border border-dashed border-[#e5e7eb] px-3.5 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-[#717182]">{displayNameForKey(m.id)}</span>
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#F5F7FA] border border-[#e5e7eb] text-[#717182]">
            Not evaluated
          </span>
          <span className="text-[11px] text-[#717182] italic">Not yet evaluated for this use case.</span>
        </div>
      </div>
    );
  }

  return (
    <div id={measureDomId(m.id)} className="bg-[#F5F7FA] rounded-lg border border-[#e5e7eb]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 flex-wrap px-3.5 py-2.5 text-left"
      >
        {open ? <ChevronUp className="w-3.5 h-3.5 text-[#717182]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#717182]" />}
        <span className="text-xs font-bold text-[#18365E]">{displayNameForKey(m.id)}</span>
        {cue && <span className="text-[10px] text-[#717182]">(higher = {cue})</span>}
        <span className="ml-auto flex items-center gap-2">
          {m.scale === '1to5'
            ? <ScorePips value={m.raw ?? 0} />
            : <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${scoreCls(m.raw ?? 0)}`}>{(m.raw ?? 0).toFixed(1)}</span>}
          {m.weight && (
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${WEIGHT_CLS[m.weight] ?? WEIGHT_CLS.Low}`}>
              {m.weight} weight
            </span>
          )}
        </span>
      </button>

      {open && (
        <div className="px-3.5 pb-3.5">
          <div className="flex items-center gap-2 flex-wrap">
            {m.source && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-white border border-[#e5e7eb] text-[#717182]">
                {sourceLabel(m.source)}
              </span>
            )}
            {/* Confidence renders ONLY when the warehouse actually has one. */}
            {m.conf !== null && m.conf !== undefined && (
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${confPill(m.conf)}`}>{pct(m.conf)}% conf</span>
            )}
          </div>
          <p className="text-xs text-[#4b5563] leading-relaxed mt-1.5">{m.rat}</p>

          {m.ev.length > 0 && (
            <button
              onClick={() => setShowEv((v) => !v)}
              className="mt-2 text-[10px] font-bold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1"
            >
              {showEv ? <ChevronUp className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {showEv ? 'Hide evidence' : `Show evidence (${m.ev.length})`}
            </button>
          )}
          {showEv && (
            <div className="mt-2 space-y-1.5">
              {m.ev.map((e, i) => {
                const EIcon = EV_ICON[e.type] ?? FileText;
                return (
                  <div key={i} className="bg-white border border-[#e5e7eb] rounded-md px-3 py-2">
                    <p className="text-[10px] font-semibold text-[#18365E] flex items-center gap-1.5">
                      <EIcon className="w-3 h-3 text-[#717182]" />
                      {evidenceSourceLabel(e, m)}
                      <span className="font-medium text-[#717182] uppercase text-[9px] tracking-wide ml-auto">{e.type}</span>
                    </p>
                    <p className="text-[11px] text-[#4b5563] leading-relaxed mt-1 italic">&ldquo;{e.excerpt}&rdquo;</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * How the client's profile moved this use case's axes, in plain words, with the real values and
 * priority names. Technical record (archetype, tiers, slug, contract version, the delta-row
 * sentence) lives in the Data provenance disclosure at the bottom of the tab.
 * Exported for tests; the tab renders it internally.
 */
export function ProfileAdjustment({ delta, themes }: {
  delta: PersonalizationDeltaRow;
  themes?: PriorityThemeRow[];
}) {
  // Detected themes that did NOT drive the adjustment; the actives are named in the sentence.
  const activeSet = new Set(delta.activePriorities.map((p) => p.trim().toLowerCase()));
  const otherThemes = (themes ?? []).filter((t) => !activeSet.has(t.label.trim().toLowerCase()));

  return (
    <div data-testid="profile-adjustment">
    <BandedCard icon={<Sparkles className="w-3.5 h-3.5" />} title="How your profile adjusted this score" bodyClassName="p-5">
      <p className="text-sm text-[#4b5563] leading-relaxed">
        Your profile {adjustmentPhrase('business value', delta.bvDelta)} and{' '}
        {adjustmentPhrase('implementation ease', delta.easeDelta)}, because your active priorities include{' '}
        {joinList(delta.activePriorities)}.
      </p>
      {otherThemes.length > 0 && (
        <p className="text-xs text-[#717182] leading-relaxed mt-2">
          Also detected, not applied to this use case: {joinList(otherThemes.map((t) => t.label))}.
        </p>
      )}
    </BandedCard>
    </div>
  );
}

/**
 * One page-level line naming exactly where the per-measure layer came from, including the
 * RAW source_type values, which the per-measure chips deliberately present in plain language.
 */
function ProvenanceLine({ p, rawSources }: { p: RationaleProvenanceRow; rawSources: string[] }) {
  return (
    <p className="text-[10px] text-[#717182] leading-relaxed flex items-start gap-1.5">
      <Database className="w-3 h-3 mt-[1px] shrink-0" />
      <span>
        Per-measure rationale, weights and sources read from{' '}
        <span className="font-semibold text-[#18365E]">{p.project}.{p.dataset}.{p.table}</span>{' '}
        · client {p.clientId} · use case {p.useCaseId} · correlation {p.correlationId} ·{' '}
        {p.contractVersion} · extracted {p.extractedAt}. Scores are on the warehouse 1 to 5 scale.
        {rawSources.length > 0 && (
          <> Source type: {rawSources.map((r) => sourceLabel(r).toLowerCase()).join(', ')}.</>
        )}
      </span>
    </p>
  );
}

export interface ScoringRationaleTabProps {
  brief: DecisionBrief;
}

export const ScoringRationaleTab: React.FC<ScoringRationaleTabProps> = ({ brief }) => {
  const sc = getScores(brief);
  // Everything starts collapsed: groups and measure rows alike.
  const [openDims, setOpenDims] = useState<string[]>([]);
  const [openMeasures, setOpenMeasures] = useState<string[]>([]);
  const toggleDim = (id: string) => setOpenDims((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));
  const toggleMeasure = (id: string) => setOpenMeasures((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]));

  // Clicking a strongest/weakest line opens its group and row, then scrolls to it once rendered.
  const pendingScroll = useRef<string | null>(null);
  const jumpTo = (dimId: string, measureId: string) => {
    setOpenDims((x) => (x.includes(dimId) ? x : [...x, dimId]));
    setOpenMeasures((x) => (x.includes(measureId) ? x : [...x, measureId]));
    pendingScroll.current = measureDomId(measureId);
  };
  useEffect(() => {
    const id = pendingScroll.current;
    if (!id) return;
    pendingScroll.current = null;
    document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [openDims, openMeasures]);

  // Distinct raw source_type values across every measure, shown in the provenance disclosure.
  const rawSources = Array.from(
    new Set(sc.dims.flatMap((d) => d.measures.map((m) => m.source).filter(Boolean) as string[])),
  ).sort();
  const traces = collectEvidenceTraces(sc.dims);

  // Tier C (spec section 5): no per-measure layer exists for this use case in either warehouse, so
  // no measure rows are fabricated. The dimension bars still render; this line says why there is
  // nothing beneath them. Never show invented measures to fill the space.
  const isTierC = sc.dims.every((d) => d.measures.length === 0);
  const hasGaps = sc.coverage.evaluated < sc.coverage.total;
  const { strongest, weakest } = strongestAndWeakest(sc.dims);
  const dimName = (id: string) => DIM_META.find((x) => x.id === id)?.name ?? id;
  const breakdown = compositeBreakdown(sc.dims, sc.compositeWeights, DIM_META.map((m) => m.id as DimensionVM['id']));
  const anyBurden = sc.dims.some((d) => d.measures.some((m) => m.evaluated && burdenCue(m)));

  const FactorLine = ({ r }: { r: { measure: MeasureVM; dim: DimensionVM } }) => {
    const cue = burdenCue(r.measure);
    return (
      <li>
        <button
          type="button"
          onClick={() => jumpTo(r.dim.id, r.measure.id)}
          className="w-full text-left text-xs text-[#4b5563] hover:text-[#18365E] flex items-baseline gap-1.5 flex-wrap"
        >
          <span className="font-bold text-[#18365E]">{displayNameForKey(r.measure.id)}</span>
          <span className="tabular-nums">{formatMeasureScore(r.measure)}</span>
          {cue && <span className="text-[#717182]">(higher = {cue})</span>}
          <span className="text-[#717182]">in {dimName(r.dim.id)}</span>
        </button>
      </li>
    );
  };

  return (
    <div className="space-y-4">
      {/* ── 1 · WHY IT SCORED N ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
        <SectionHeaderBand
          icon={<BarChart3 className="w-3.5 h-3.5" />}
          title={`Why it scored ${sc.total}`}
          subtitle={
            isTierC
              ? 'Evaluated via the standard model; measure-level detail generates as engagement deepens.'
              : sc.compositeWeights
                ? 'The composite is a weighted average: each dimension\u2019s score times its weight, summed.'
                : 'Each dimension shows its score and its strongest measure.'
          }
          right={
            <>
              <AsOfChip />
              {/* Coverage is stated up front rather than left to be inferred from the gaps below. */}
              {!isTierC && sc.coverage.total > 0 && (
                <span className="text-[11px] text-[#4b5563]">
                  <span className="font-semibold text-[#18365E]">
                    {sc.coverage.evaluated} of {sc.coverage.total} measures evaluated
                  </span>
                  {hasGaps && (
                    <> · unevaluated measures are excluded from every rollup <sup className="font-bold text-[#18365E]">†</sup></>
                  )}
                </span>
              )}
              {/* Model-confidence pill renders only when the warehouse supplies one. */}
              {sc.confidence !== null && sc.confidence !== undefined && (
                <span className={`text-[9px] font-bold px-2 py-1 rounded-full ${confPill(sc.confidence)}`}>Model confidence {pct(sc.confidence)}%</span>
              )}
            </>
          }
        />
        <div className="p-5">
        {breakdown && (
          <div className="mb-5" data-testid="composite-breakdown">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 rounded-full bg-[#F5F7FA] border border-[#e5e7eb] overflow-hidden flex">
                {breakdown.map((seg, i) => (
                  <div
                    key={seg.id}
                    data-testid={`composite-segment-${seg.id}`}
                    title={`${dimName(seg.id)}: ${seg.score.toFixed(1)} \u00d7 ${dimWeightPct(sc.compositeWeights!, seg.id)}% = ${seg.points.toFixed(1)} pts`}
                    className={i < breakdown.length - 1 ? 'border-r-2 border-white' : ''}
                    style={{ width: `${seg.points}%`, background: SEGMENT_FILL[seg.id] }}
                  />
                ))}
              </div>
              <span className="text-lg font-bold text-[#18365E] tabular-nums" aria-label="Composite score">{sc.total}</span>
            </div>
            <p className="text-[11px] text-[#717182] mt-1.5">
              {breakdown.map((seg, i) => (
                <React.Fragment key={seg.id}>
                  {i > 0 && ' · '}
                  <span
                    aria-hidden="true"
                    data-testid={`composite-swatch-${seg.id}`}
                    className="inline-block w-2 h-2 rounded-[2px] mr-1 align-baseline"
                    style={{ background: SEGMENT_FILL[seg.id] }}
                  />
                  {DIM_SHORT[seg.id]} {seg.points.toFixed(1)}
                </React.Fragment>
              ))}
            </p>
          </div>
        )}
        <div className="space-y-3.5">
          {DIM_META.map((meta) => {
            const d = sc.dims.find((x) => x.id === meta.id);
            if (!d) return null;
            const bar = dimBar(d);
            const best = strongestInDimension(d);
            return (
              <div key={meta.id}>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-semibold text-[#18365E]">
                    {meta.name}
                  </span>
                  <span className="font-bold text-[#18365E]">
                    {sc.compositeWeights
                      ? `${d.score.toFixed(1)} \u00d7 ${dimWeightPct(sc.compositeWeights, d.id)}% = ${(d.score * sc.compositeWeights[d.id]).toFixed(1)} pts`
                      : bar.label}
                    {d.coverage.evaluated < d.coverage.total && (
                      <sup
                        title={d.rollup === 'measures'
                          ? 'Mean of this dimension\u2019s evaluated measures'
                          : 'Portfolio-level dimension score; not a rollup of the measures listed'}
                      >†</sup>
                    )}
                  </span>
                </div>
                <div className="h-1.5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-full overflow-hidden mt-0.5">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${bar.widthPct}%`, background: meta.id === 'feasibility' ? '#FF6E4C' : '#18365E' }}
                  />
                </div>
                {best && (
                  <p className="text-[11px] text-[#717182] mt-1">
                    Strongest measure: {displayNameForKey(best.id)} ({formatMeasureScore(best)}
                    {best.weight ? `, ${best.weight} weight` : ''})
                  </p>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {!isTierC && (
        <>
          {/* ── 2 · STRONGEST / WEAKEST MEASURES ─────────────────────────── */}
          <div className="grid sm:grid-cols-2 gap-4">
            <BandedCard icon={<TrendingUp className="w-3.5 h-3.5" />} title="Strongest measures" bodyClassName="p-5">
              <ul className="space-y-2">{strongest.map((r) => <FactorLine key={r.measure.id} r={r} />)}</ul>
            </BandedCard>
            <BandedCard icon={<AlertTriangle className="w-3.5 h-3.5" />} title="Weakest measures" bodyClassName="p-5">
              <ul className="space-y-2">{weakest.map((r) => <FactorLine key={r.measure.id} r={r} />)}</ul>
            </BandedCard>
          </div>
          <p className="text-[10px] text-[#717182] -mt-2">
            Ranked by weight, then score. Select a measure to see its rationale below.
          </p>

          {/* ── 3 · ALL MEASURES ─────────────────────────────────────────── */}
          <div className="rounded-xl border border-[#e5e7eb] overflow-hidden bg-white">
            <SectionHeaderBand
              icon={<List className="w-3.5 h-3.5" />}
              title="All measures"
              subtitle={anyBurden ? 'Unless a row says otherwise, a higher score is better.' : 'A higher score is better.'}
              right={
                <span className="flex gap-2">
                  <button onClick={() => setOpenDims(DIM_META.map((d) => d.id))} className="text-[11px] font-semibold text-[#18365E] hover:text-[#FF6E4C]">Expand all</button>
                  <span className="text-[#4b5563]">·</span>
                  <button onClick={() => { setOpenDims([]); setOpenMeasures([]); }} className="text-[11px] font-semibold text-[#18365E] hover:text-[#FF6E4C]">Collapse all</button>
                </span>
              }
            />
          </div>

          {DIM_META.map((meta) => {
            const d = sc.dims.find((x) => x.id === meta.id);
            if (!d) return null;
            const isOpen = openDims.includes(meta.id);
            const DIcon = DIM_ICON[meta.icon] ?? Target;
            const bar = dimBar(d);
            return (
              <div key={meta.id} className="bg-white rounded-xl border border-[#e5e7eb]">
                <button onClick={() => toggleDim(meta.id)} aria-expanded={isOpen} className="w-full flex items-center gap-3 px-5 py-3.5 text-left group">
                  <span className="w-8 h-8 rounded-lg bg-[#F5F7FA] border border-[#e5e7eb] text-[#18365E] flex items-center justify-center shrink-0">
                    <DIcon className="w-4 h-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-[#18365E]">{meta.name}</span>
                    <span className="block text-[10px] text-[#717182]">
                      {d.coverage.evaluated === d.coverage.total
                        ? `${d.coverage.total} scored measures`
                        : `${d.coverage.evaluated} of ${d.coverage.total} measures evaluated`}
                    </span>
                  </span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-lg shrink-0 ${scoreCls(d.score)}`}>
                    {bar.label}
                  </span>
                  <span className="text-[#717182] group-hover:text-[#FF6E4C]">{isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5">
                    <p className="text-xs text-[#717182] leading-relaxed mb-3">{meta.desc}</p>
                    <div className="space-y-2">
                      {d.measures.map((m) => (
                        <MeasureRow
                          key={m.id}
                          m={m}
                          open={openMeasures.includes(m.id)}
                          onToggle={() => toggleMeasure(m.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {hasGaps && (
        <p className="text-[10px] text-[#717182] leading-relaxed flex items-start gap-1.5">
          <span className="font-bold text-[#18365E] mt-[1px]">†</span>
          <span>
            Measures marked &ldquo;Not evaluated&rdquo; carry no score and are excluded from every rollup. No
            value is substituted for them.{' '}
            {sc.dims.some((d) => d.rollup === 'measures' && d.coverage.evaluated < d.coverage.total)
              && 'Marked dimension scores are the mean of their evaluated measures. '}
            {sc.dims.some((d) => d.rollup === 'authored' && d.coverage.evaluated < d.coverage.total)
              && 'Dimension scores on this page are portfolio-level and do not derive from the measures listed, so a gap does not move them.'}
          </span>
        </p>
      )}

      {/* ── 5 · HOW YOUR PROFILE ADJUSTED THIS SCORE ─────────────────────── */}
      {sc.personalizationDelta && (
        <ProfileAdjustment delta={sc.personalizationDelta} themes={sc.priorityThemes} />
      )}

      {/* Everything internal (slugs, contract versions, table names, correlation ids, raw
          source_type values, evidence trace lines) lives here, closed by default.
          Traceability is kept, not shown. */}
      {(sc.provenance || sc.personalizationDelta || traces.length > 0) && (
        <details className="group bg-white rounded-xl border border-[#e5e7eb] px-4 py-3">
          <summary className="cursor-pointer select-none text-xs font-semibold text-[#18365E] flex items-center gap-1.5 list-none [&::-webkit-details-marker]:hidden">
            <Database className="w-3.5 h-3.5 text-[#717182]" />
            Data provenance
            <ChevronDown className="w-3.5 h-3.5 text-[#717182] ml-auto transition-transform group-open:rotate-180" />
          </summary>
          <div className="mt-3 space-y-2">
            <p className="text-[11px] text-[#4b5563] leading-relaxed">
              Scores, weights and sources come from the DVA evaluation warehouse.
            </p>
            {sc.provenance && <ProvenanceLine p={sc.provenance} rawSources={rawSources} />}
            {sc.personalizationDelta && (
              <div className="text-[10px] text-[#717182] leading-relaxed space-y-1">
                <p>
                  Profile: {sc.personalizationDelta.archetype} ({sc.personalizationDelta.archetypeSlug}) ·
                  relevance tier {sc.personalizationDelta.relevanceTier} · intensity tier {sc.personalizationDelta.intensityTier}.
                </p>
                <p>
                  Recorded on the p4 delta row ({sc.personalizationDelta.contractVersion}). The dimension scores
                  above already include these adjustments; the composite is computed from the authored scoring
                  root and does not re-apply them.
                </p>
              </div>
            )}
            {traces.length > 0 && (
              <details className="text-[10px] text-[#717182]">
                <summary className="cursor-pointer font-semibold text-[#18365E]">Evidence trace ({traces.length} distinct sources)</summary>
                <ul className="mt-1.5 space-y-1">
                  {traces.map((t) => (
                    <li key={t.src} className="leading-relaxed">
                      <span className="font-mono text-[9px] text-[#4b5563]">{t.src}</span>
                      <span> · {joinList(t.measureIds.map((id) => displayNameForKey(id)))}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

export default ScoringRationaleTab;
